import { ButtonAction, Icon } from '@honeycomb-npm/honeycomb-react';
import { DynamicContent } from '@commblades/commblades';

import {
    DynamicMessageWrap,
    HiddenDynamicMessageWrap,
    LoanItemHeaderDivWrapper,
    LoanItemHrWrapper,
    LoanItemLearnMoreDivWrapper,
    LoanItemOfferDivWrapper,
    LoanItemRepaymentContainerWrapper,
    LoanItemWrapper,
    LoanOptionWrapper,
    OfferWrapper,
    RecommendPrimarySpanWrapper,
    RecommendSecondarySpanWrapper,
} from './LoanPresentation.styled';

export interface SimpleLoanOptionContentParams {
    cappedMessage?: string[]
    repaymentHeading?: string[]
    repaymentDescription?: string[]
    dynamicMessage?: string[]
}

export type DynamicMessageInfo ={
    message: string,
    isHidden: boolean,
}

export type SimpleLoanOption = {
    isRecommendedPrimary?: boolean;
    isRecommendedPrimaryText?: string;
    isRecommendedSecondary?: boolean;
    isRecommendedSecondaryText?: string;
    isRecommendedOffer?: boolean;
    id: number;
    heading: string;
    cappedMessage?: string;
    repayment?: {
        heading: string;
        description?: string;
        amount: number;
        frequency: string;
        bottomDescription?: string;
    };
    offer?: {
        heading: string;
        description: string[];
    };
    dynamicMessageInfo?:DynamicMessageInfo;
    repaymentFrequency?: string;
    params?: SimpleLoanOptionContentParams;
    collapsed?: boolean;
    hyperLinkText?: string;
    hyperOpenLink?: string;
};

export const LoanPresentation = ({
    isRecommendedPrimary = false,
    isRecommendedPrimaryText,
    isRecommendedSecondary = false,
    isRecommendedSecondaryText,
    isRecommendedOffer = false,
    id,
    heading,
    cappedMessage,
    repayment,
    repaymentFrequency = 'Weekly',
    offer,
    dynamicMessageInfo,
    params,
    collapsed,
    hyperLinkText,
    hyperOpenLink,
}: SimpleLoanOption) => {
    const openLearnMoreLink = () => {
        window.open(hyperOpenLink, '_blank');
    };

    return (
        <LoanItemWrapper id={`loan-option-${id}`}>
            {isRecommendedPrimary && (
                <RecommendPrimarySpanWrapper>
                    {isRecommendedPrimaryText}
                </RecommendPrimarySpanWrapper>
            )}
            {isRecommendedSecondary && (
                <RecommendSecondarySpanWrapper>
                    {isRecommendedSecondaryText}
                </RecommendSecondarySpanWrapper>
            )}
            <LoanOptionWrapper>
                {cappedMessage && (
                    <>
                        <div>
                            <LoanItemHeaderDivWrapper>
                                <DynamicContent
                                    isParagraph
                                    className="mt-2"
                                    content={cappedMessage}
                                    params={params?.cappedMessage}
                                    id={`loan-option-${id}-capped-description`}
                                />
                                {isRecommendedOffer && (
                                    <Icon
                                        alt=""
                                        className="mb-3 pl-2"
                                        symbol="messageOfferSash40"
                                    />
                                )}
                            </LoanItemHeaderDivWrapper>
                        </div>
                        <div><LoanItemHrWrapper aria-hidden="true" /></div>
                    </>
                )}
                <LoanItemRepaymentContainerWrapper>
                    {repayment?.description && (
                        <DynamicContent
                            isParagraph
                            className="mb-1"
                            content={repayment.description}
                            id={`loan-option-${id}-repayment-description`}
                        />
                    )}
                    {repayment?.bottomDescription && (
                        <DynamicContent
                            isParagraph
                            className="pt-2"
                            content={repayment.bottomDescription}
                            id={`loan-option-${id}-repayment-bottom-description`}
                        />
                    )}
                </LoanItemRepaymentContainerWrapper>
                <LoanItemOfferDivWrapper>
                    {offer && (
                        <Icon
                            alt=""
                            className="mb-0 mt-0"
                            symbol="messageSuccess16"
                        >
                            <OfferWrapper id={`loan-option-${id}-offer-heading`}>
                                <DynamicContent
                                    content={offer.heading}
                                    id={`loan-option-${id}-offer-description`}
                                />
                            </OfferWrapper>
                        </Icon>
                    )}
                </LoanItemOfferDivWrapper>
                {dynamicMessageInfo && !dynamicMessageInfo.isHidden && (
                    <DynamicMessageWrap>
                        <Icon
                            alt=""
                            symbol="messageInfo16"
                        >
                            <OfferWrapper id={`loan-option-${id}-dynamic-message`}>
                                <DynamicContent
                                    content={dynamicMessageInfo.message}
                                    params={params?.dynamicMessage}
                                    id={`loan-option-${id}-dynamic-message`}
                                />
                            </OfferWrapper>
                        </Icon>
                    </DynamicMessageWrap>
                )}
                {!collapsed && dynamicMessageInfo && dynamicMessageInfo.isHidden && (
                    <HiddenDynamicMessageWrap>
                        <Icon
                            alt=""
                            symbol="messageInfo16"
                        >
                            <OfferWrapper id={`loan-option-${id}-dynamic-message`}>
                                <DynamicContent
                                    content={dynamicMessageInfo.message}
                                    params={params?.dynamicMessage}
                                    id={`loan-option-${id}-dynamic-message`}
                                />
                            </OfferWrapper>
                        </Icon>
                    </HiddenDynamicMessageWrap>
                )}
                {hyperLinkText && (
                    <>
                        <LoanItemHrWrapper aria-hidden="true" className="m-0" />
                        <LoanItemLearnMoreDivWrapper>
                            <ButtonAction
                                variant="subtle"
                                aria-labelledby={`learn-more-${heading.replace(/\s+/g, '').toLocaleLowerCase()}-loan-hyperlink`}
                                id={`loan-option-${id}-learn-more-${heading.replace(/\s+/g, '').toLocaleLowerCase()}-loan-hyperlink`}
                                onClick={openLearnMoreLink}
                            >
                                {hyperLinkText}
                            </ButtonAction>
                        </LoanItemLearnMoreDivWrapper>
                    </>
                )}
            </LoanOptionWrapper>
        </LoanItemWrapper>
    );
};
